import { HandleDragActivity } from 'components/planner/GraphProps';
import { changeRectActivities } from 'components/planner/utils/edit';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { Vector2d } from 'konva/lib/types';
import { useCallback, useMemo, useRef } from 'react';
import { Group, Label, Line, Rect, Tag, Text } from 'react-konva';
import { DateNumber } from 'types/global';
import { diffDay } from 'utils/time';
import { Activity, GraphGroup, GraphRect, GraphStyle } from "../../types";
import { getRectsFromActivities, translateRects, xFromTimeStyled } from "../../utils";



/**
 * Visualizza delle grafiche aggiuntive sul CYCLE-ACTIVITY selezionato
 */
function ActivitySelect({
	readOnly,
	dateStart,
	dateCurrent,

	activitiesSelect,
	groups,
	onChangeGroups,
	onDrag,
	onDragStart,
	onDragEnd,

	height,
	scrollY = 0,
	style,
	...props
}: ActivitySelectProps) {

	// HOOKs
	/** la riga cliccata quando inizia il drag per l'editazione */
	const lineStartDrag = useRef(-1)
	/** i giorni di scostamento dalla posizione originale dovuti al drag per editazione */
	const offsetDays = useRef(0)
	/**PlaceHolder per il DRAG delle righe verticali */
	const refPlaceholder = useRef(null)



	const getRects = useCallback((activitiesSelect: Activity[], groups: GraphGroup[], dateStart: number, style: GraphStyle) => {
		return getRectsFromActivities(activitiesSelect, groups, dateStart, style)
	}, [activitiesSelect, groups, dateStart, style])

	/**
	 * un array di RECT che delimita i margini di ogni GROUP delle ACTIVITIES-SELECT
	 * e contiene le ACTIVITIES di quel RECT
	 */
	const { rects, rectCycles, timeMin, timeMax, linesVert, linesOriz } = useMemo(() => {
		let { rects, timeMin, timeMax } = getRects(activitiesSelect, groups, dateStart, style)
		rects = translateRects(rects, scrollY, dateStart, dateCurrent, style)
		// scorporo il rect del gruppo CYCLES
		let rectCycles: GraphRect = null
		rects = rects.filter(rect => {
			if (rect.group.name == "cycles") {
				rectCycles = rect
				return false
			}
			return true
		})
		const { linesVert, linesOriz } = getLine(rects, readOnly ? null : rectCycles, style)
		return { rects, rectCycles, timeMin, timeMax, linesVert, linesOriz }
	}, [getRects, scrollY, dateStart, dateCurrent, style])



	//#region HANDLEs

	const handleMouseEnter = (e) => {
		const container = e.target.getStage().container();
		container.style.cursor = "col-resize";
	}
	const handleMouseLeave = (e) => {
		const container = e.target.getStage().container();
		container.style.cursor = "default";
	}

	/** Inizio del DRAG quando si è in EDIT */
	const handleMouseDown = (e: KonvaEventObject<MouseEvent>, index: number) => {
		const points = (e.target as Konva.Line).points() as number[]
		const placeholder = refPlaceholder.current as Konva.Line
		placeholder.x(0)
		placeholder.y(0)
		//placeholder.points(points)
		placeholder.points([points[0], 0, points[0], height + scrollY])
		placeholder.visible(true)
		placeholder.startDrag()

		lineStartDrag.current = index
		offsetDays.current = 0
	}


	/** il DRAG è iniziato */
	const handleDragStart = e => {
		onDragStart?.()
	}
	/** Quando il mouse ha premuto su una barretta verticale e la sta draggando */
	const handleDragMove = (e: KonvaEventObject<DragEvent>) => {

		// calcolo lo scostamento in giorni rispetto al riposizionamento dell placeholder
		const placeholder = refPlaceholder.current
		if (!placeholder) return

		const offset = Math.round(placeholder.x() / style.space.x)
		const deltaOffset = offset - offsetDays.current
		if (deltaOffset == 0) return

		// se c'e' uno scostamento rilevante provo a modificare le ACTIVITY nel PLANNER
		if (!changeRectActivities(deltaOffset, lineStartDrag.current, rects, rectCycles)) return
		offsetDays.current = offset

		// se non ci sono problemi invio la modifica
		onChangeGroups([...groups])
		onDrag?.(deltaOffset, lineStartDrag.current, rects)
	}
	/** il DRAG è terminato */
	const handleDragEnd = e => {
		const placeholder = refPlaceholder.current
		placeholder?.visible(false)
		onDragEnd?.(offsetDays.current, lineStartDrag.current, rects)
	}


	// ******** FULL CYCLE DRAG *************** 
	// const dragYStart = useRef(0)
	// const handleDragStart2 = (e: KonvaEventObject<DragEvent>) => {
	// 	console.log("handleDragStart2")
	// 	const rect = e.target as Konva.Rect
	// 	lineStartDrag.current = -1
	// 	offsetDays.current = 0
	// 	onDragStart?.()
	// }
	// const handleDragMove2 = (e: KonvaEventObject<DragEvent>) => {
	// 	console.log("handleDragMove2")
	// 	const rect = e.target as Konva.Rect
	// 	const offset = Math.ceil(rect.x() / style.space.x)
	// 	const deltaOffset = offset - offsetDays.current
	// 	if (deltaOffset == 0) return
	// 	onDrag?.(delta, -1, rects)
	// }
	// const handleDragEnd2 = e => {
	// 	console.log("handleDragEnd2")
	// 	const rect = e.target as Konva.Rect
	// 	const delta = rect.y() - dragYStart.current
	// 	onDragEnd?.(delta, -1, rects)
	// }
	// function callbackDrag2(pos: Vector2d) {
	// 	const posRel = this.absolutePosition()
	// 	pos.y = posRel.y
	// 	return pos
	// }
	// ******** FULL CYCLE DRAG *************** 



	//#endregion


	// RENDER

	// RENDER
	if (!rects || !activitiesSelect || !groups || activitiesSelect.length == 0) return null

	const xFromTime = xFromTimeStyled(style, dateCurrent)
	const marginTop = style.activity.selection.margin.top
	const lineDragColor = style.activity.selection.drag.color

	// il colore da utilizzare. Il primo che trovo nelle ACTIVITIES-SELECT
	// TODO: memo
	const color = activitiesSelect[0].color ?? style.activity.selection.defaultColor

	// numero di giorni della durata di tutte le attività
	const numDays = diffDay(timeMax, timeMin)
	// le linee tratteggiate laterali min e max
	const linesVertDash = [xFromTime(timeMin) + style.space.x2, xFromTime(timeMax) - style.space.x2]
	// larghezza del label dei giorni
	const labelWidth = 10 * numDays.toString().length
	// posizione x del label dei giorni
	const labelX = (linesVertDash[0] + ((linesVertDash[1] - linesVertDash[0]) / 2)) - (labelWidth / 2)
	// posixione y del label dei giorni
	const labelY = marginTop - 6

	/** Utilizzato per delimitare i limiti del drag */
	const callbackDrag = (pos: Vector2d) => {
		const posRel = refPlaceholder.current.absolutePosition()
		pos.y = posRel.y
		return pos
	}




	return (<Group {...props}>


		{/* {rects.map((rect, index) => (
			<Rect key={index}
				listening={false}
				x={rect.p1x}
				y={rect.p1y}
				width={rect.p2x - rect.p1x}
				height={rect.p2y - rect.p1y}
				stroke='red'
				strokeWidth={2}
			/>
		))} */}


		{/* Linee verticali tratteggiate */}
		{linesVertDash.map((x, index) => (
			<Line key={index}
				listening={false}
				points={[x, 0, x, height + scrollY]}
				stroke={lineDragColor} strokeWidth={1} dash={[5, 5]}
			/>
		))}

		{/* Le linee verticali che delimitano i GOUP-ACTIVITIES */}
		{linesVert.map((line, index) => (
			<Line key={index}
				points={[line.p1x, line.p1y, line.p2x, line.p2y]}
				stroke={color}
				listening={!readOnly}
				strokeWidth={readOnly ? 1 : 5}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onMouseDown={(e) => handleMouseDown(e, index)}
			/>
		))}

		{/* Le linne orizzontali dovute ai GROUP-COLLAPSED */}
		{linesOriz.map((line, index) => (
			<Line key={index}
				listening={false}
				points={[line.p1x, line.p1y, line.p2x, line.p2y]}
				stroke={color}
				strokeWidth={2}
			/>
		))}

		{/* Linea e scritta del totale di giorni */}
		<Line
			listening={false}
			points={[linesVertDash[0] - style.space.x, marginTop, linesVertDash[1] + style.space.x, marginTop]}
			stroke={color}
			strokeWidth={1}
		/>
		<Label x={labelX} y={labelY} listening={false}>
			<Tag fill='#000000' />
			<Text
				text={numDays.toString()}
				fontSize={12}
				fill={color}
				//fontFamily="Roboto"
				align="center" verticalAlign="middle"
				width={labelWidth}
			/>
		</Label>

		{/* placeholder drag  */}
		<Line ref={refPlaceholder}
			stroke={style.primaryColor}
			strokeWidth={2}
			dash={[5, 5]}
			onDragMove={handleDragMove}
			onDragEnd={handleDragEnd}
			onDragStart={handleDragStart}
			dragBoundFunc={callbackDrag}
		/>

		{/* ******** FULL CYCLE DRAG *************** 
		<Rect
			x={rectCycles.p1x}
			y={rectCycles.p1y}
			width={rectCycles.p2x - rectCycles.p1x}
			height={rectCycles.p2y - rectCycles.p1y}
			fill="red"
			draggable
			strokeWidth={2}
			onDragStart={handleDragStart2}
			onDragMove={handleDragMove2}
			onDragEnd={handleDragEnd2}
			dragBoundFunc={callbackDrag2}
		/> */}

	</Group>)
}

export default ActivitySelect

type ActivitySelectProps = {
	/** se il GANTT è in modalit' EDIT o READ */
	readOnly: boolean,
	/** la data di riferimento iniziale di tutto il PLANNER */
	dateStart: DateNumber,
	/** la data correntemente visibile a sinsitra del PLANNER */
	dateCurrent: DateNumber,
	// solo le CYCLE-ACTIVITY da selezionare
	activitiesSelect: Activity[],

	groups: GraphGroup[],
	/** Quando qualcosa del gruppo cambia. tipicamente ho modificate le dimensioni di un ACTIVITY */
	onChangeGroups: (groups: GraphGroup[]) => void
	/** quando sto spostando qualcosa */
	onDrag?: HandleDragActivity
	/** quando ho iniziato lo spostamento */
	onDragStart?: () => void
	/** quando ho finito lo spostamento */
	onDragEnd?: HandleDragActivity

	/** altezza del  */
	height: number,
	/** devo tenere conto anche dello scrolling Y naturalmente */
	scrollY: number,
	style: GraphStyle,
} & Konva.NodeConfig



function getLine(
	rects: GraphRect[],
	opRect: GraphRect,
	style: GraphStyle,
): { linesVert?: Line[], linesOriz?: Line[] } {
	if (!rects || !style) return {}

	return rects.reduce((acc, rect, index) => {
		if (!rect.p1x) return acc

		// prima riga inizia da sopra vicino alla timeline
		if (index == 0) {
			acc.linesVert.push({ p1x: rect.p1x, p2x: rect.p1x, p1y: 0, p2y: opRect?.p2y ?? rect.p2y })
		}
		// anche l'ultima riga va a finire sopra
		if (index == rects.length - 1) {
			acc.linesVert.push({ p1x: rect.p2x, p2x: rect.p2x, p1y: 0, p2y: opRect?.p2y ?? rect.p2y })

			// riga verticale di collegamento al "rect" seguente
		} else {
			const nextRect = rects[index + 1]
			let yCurrent = rect.p2y
			let yNext = nextRect.p1y
			if (nextRect.p1y > rect.p2y) {
				yCurrent = rect.p1y
				yNext = opRect?.p2y ?? nextRect.p2y
			}
			acc.linesVert.push({ p1x: rect.p2x, p2x: rect.p2x, p1y: yNext, p2y: yCurrent })
		}
		// se il gruppo è collapsed allora disegna una riga orizzontale
		if (rect.collapsed) {
			acc.linesOriz.push({ p1x: rect.p1x + 2, p2x: rect.p2x - 2, p1y: rect.p1y - style.space.y2, p2y: rect.p1y - style.space.y2 })
		}

		return acc

	}, { linesVert: [], linesOriz: [] })
}

type Line = {
	p1x: number,
	p2x: number,
	p1y: number,
	p2y: number,
}
